import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [tokenMissing, setTokenMissing] = useState(false);
  const [pw, setPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = searchParams.get("token");
    if (!t) {
      setTokenMissing(true);
      toast.error("Lien invalide : token manquant.");
      const timer = setTimeout(() => navigate("/login"), 3200);
      return () => clearTimeout(timer);
    }
    setToken(t);
  }, [searchParams, navigate]);

  const validate = () => {
    if (pw.length < 8) return "Minimum 8 caracteres.";
    if (!/[A-Z]/.test(pw)) return "Doit contenir au moins une majuscule.";
    if (!/\d/.test(pw)) return "Doit contenir au moins un chiffre.";
    if (pw !== pwConfirm) return "Les deux mots de passe ne correspondent pas.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Mot de passe reinitialise !");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Lien invalide ou expire.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      data-testid="reset-password-page"
    >
      <div className="absolute inset-0">
        <img
          src="/login-bg.jpg"
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour a la connexion
          </button>

          {tokenMissing ? (
            <div className="text-center py-8" data-testid="reset-token-missing">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <span className="text-3xl text-red-600">!</span>
              </div>
              <h1 className="text-xl font-semibold mb-2 text-red-700">
                Lien invalide : token manquant
              </h1>
              <p className="text-sm text-gray-600">
                Ce lien est incomplet. Redirection vers la connexion...
              </p>
            </div>
          ) : done ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <h1 className="text-xl font-semibold mb-2">Mot de passe reinitialise</h1>
              <p className="text-sm text-gray-600">Redirection vers la connexion...</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Nouveau mot de passe
              </h1>
              <p className="text-sm text-gray-600 mb-6">
                Choisis un mot de passe securise : min 8 caracteres, 1 majuscule, 1 chiffre.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wide">
                    Nouveau mot de passe
                  </label>
                  <Input
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Min 8 caracteres, 1 majuscule, 1 chiffre"
                    autoFocus
                    data-testid="reset-pw-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wide">
                    Confirmation
                  </label>
                  <Input
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    placeholder="Retape ton mot de passe"
                    data-testid="reset-pw-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !pw || !pwConfirm}
                  className="w-full"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reinitialisation...
                    </>
                  ) : (
                    "Valider le nouveau mot de passe"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
